<script setup lang="ts">
import CDefaultTemplate from '@/presentation/components/templates/CDefaultTemplate.vue'

import { default as PToast } from 'primevue/toast'
import { default as PConfirmDialog } from 'primevue/confirmdialog'
import { default as PDynamicDialog } from 'primevue/dynamicdialog'
import { default as PProgressSpinner } from 'primevue/progressspinner'
import { default as PButton } from 'primevue/button'
import { storeToRefs } from 'pinia'
import { computed, onBeforeMount, ref, watch } from 'vue'
import VUnauthorized from '@/presentation/views/errors/VUnauthorized.vue'
import { useAppInitializer } from '@/presentation/configuration/appInitializer'
import { useRouter } from 'vue-router'
import { useAuthStore } from '@/presentation/stores/auth'
import { storeEventBus } from '@/presentation/stores/utils/storeEventBus'

// Stores et services
const authStore = useAuthStore()
const { isAuthenticated, isAllowedToLogIn, isLoading: authLoading } = storeToRefs(authStore)
const router = useRouter()
const appInitializer = useAppInitializer()

// États
const isInitializing = ref(true)
const initializationError = ref<Error | null>(null)

// Écouter les événements d'initialisation de l'application
watch(
  () => storeEventBus.lastEvent.value,
  (event) => {
    if (event?.name === 'app-initialized') {
      isInitializing.value = false

      // Routage automatique après initialisation
      if (isAuthenticated.value) {
        router.push({
          name:
            router.currentRoute.value.name === 'login'
              ? 'dashboard'
              : router.currentRoute.value.name
        })
      } else {
        authStore.loginWithRedirect()
      }
    } else if (event?.name === 'app-initialization-failed') {
      isInitializing.value = false
      initializationError.value = new Error(event.payload.error)
    } else if (event?.name === 'auth-logged-out') {
      // Rediriger vers Auth0 après déconnexion
      authStore.loginWithRedirect()
    }
  }
)

onBeforeMount(async () => {
  try {
    isInitializing.value = true

    // Démarrer l'initialisation de l'application
    await appInitializer.initialize()

    // Si le processus est terminé immédiatement (rare), on met à jour l'état
    if (appInitializer.isInitialized()) {
      isInitializing.value = false
    }
    // Sinon, l'état sera mis à jour via l'écouteur d'événements ci-dessus
  } catch (error) {
    console.error("Erreur lors de l'initialisation de l'application:", error)
    isInitializing.value = false
    initializationError.value = error instanceof Error ? error : new Error(String(error))
  }
})

// Réactions aux changements d'état d'authentification
watch(isAuthenticated, (isLoggedIn) => {
  if (isLoggedIn && !appInitializer.isInitialized()) {
    // L'utilisateur vient de se connecter, mais l'app n'est pas encore initialisée
    // Lancer l'initialisation
    appInitializer.initialize().catch((error) => {
      console.error("Erreur lors de l'initialisation après authentification:", error)
    })
  }
})

// État de chargement global
const isLoading = computed(() => {
  return isInitializing.value || authLoading.value
})
</script>

<template>
  <c-default-template :with-sidebar="$route.meta.withSidebar as boolean">
    <!-- Écran de chargement pendant l'initialisation -->
    <div v-if="isLoading" class="flex h-screen w-screen items-center justify-center">
      <div class="text-center">
        <p-progress-spinner />
        <p class="mt-4 text-gray-600">Chargement en cours...</p>
      </div>
    </div>

    <!-- Affichage de l'erreur d'initialisation -->
    <div
      v-else-if="initializationError"
      class="flex h-screen w-screen flex-col items-center justify-center"
    >
      <div class="text-center">
        <h2 class="mb-4 text-xl font-bold text-red-600">Erreur d'initialisation</h2>
        <p class="mb-2 text-gray-700">{{ initializationError.message }}</p>
        <p-button
          @click="
            () => {
              appInitializer.reset()
              appInitializer.initialize()
            }
          "
          label="Réessayer"
        />
      </div>
    </div>

    <!-- Page d'accès non autorisé -->
    <v-unauthorized v-else-if="!isAllowedToLogIn" />

    <!-- Contenu principal de l'application -->
    <RouterView v-else />

    <!-- Composants globaux -->
    <p-toast />
    <p-confirm-dialog></p-confirm-dialog>
    <p-dynamic-dialog />
  </c-default-template>
</template>
